/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
    FaFileAlt,
    FaUser,
    FaFilePdf,
    FaFileWord,
    FaDownload,
    FaCheckCircle,
    FaSpinner,
    FaCalendarAlt,
    FaBook,
    FaGraduationCap,
    FaSearch,
    FaStar,
    FaAward,
    FaInfoCircle,
    FaUserTie,
    FaEdit,
    FaUniversity,
    FaFilter,
    FaTimes,
    FaCheck,
    FaArrowLeft,
    FaTrophy,
    FaLanguage
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as professorService from '../services/professorService';
import * as masterService from '../services/masterService';

const AllArticlesYoungIndia = () => {
    const [allArticles, setAllArticles] = useState([]);
    const [filteredArticles, setFilteredArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewComment, setReviewComment] = useState('');
    const [rating, setRating] = useState('');
    const [decision, setDecision] = useState('APPROVED'); // 'APPROVED' or 'REJECTED'
    const [editingRating, setEditingRating] = useState(null);
    const [tempRating, setTempRating] = useState('');
    const [isEditorialBoard, setIsEditorialBoard] = useState(false);

    // Search filters
    const [searchStudentName, setSearchStudentName] = useState('');
    const [selectedUniversity, setSelectedUniversity] = useState('all');
    const [selectedCollege, setSelectedCollege] = useState('all');
    const [selectedTitle, setSelectedTitle] = useState('all');
    const [selectedSubject, setSelectedSubject] = useState('all');

    // Filter options
    const [universities, setUniversities] = useState([]);
    const [colleges, setColleges] = useState([]);
    const [titles, setTitles] = useState([]);
    const [subjects, setSubjects] = useState([]);

    // Selection state
    const [selectedRows, setSelectedRows] = useState([]);
    const [showSelectedModal, setShowSelectedModal] = useState(false);
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
    const [currentCardIndex, setCurrentCardIndex] = useState(0);

    const [toast, setToast] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const currentUser = authService.getCurrentUser();

        if (!currentUser) {
            navigate('/login');
            return;
        }

        const isVerifiedEB = authService.isVerifiedEditorial(currentUser);
        // Fallback for roles that should have access even if specific flags are in flux
        const hasEBPathRole = ['editorial', 'admin', 'professor'].includes(currentUser.role || currentUser.role_code);

        if (!isVerifiedEB && !hasEBPathRole) {
            setToast({ message: 'Access denied. You must be an Editorial Board member.', type: 'error' });
            navigate('/');
            return;
        }

        // Attempt to load all articles - the API will handle ultimate authorization
        loadAllArticles();
    }, [navigate]);

    const loadAllArticles = async () => {
        setLoading(true);

        try {
            const articlesData = await professorService.getAllArticles();
            const [subjectsData, streamsData, specializationsData, languagesData] = await Promise.all([
                masterService.getAllSubjects(),
                masterService.getAllStreams(),
                masterService.getAllSpecializations(),
                masterService.getAllLanguages()
            ]);

            const availableArticles = articlesData.data || articlesData.articles || articlesData;

            // Helper to extract data from potentially wrapped responses
            const unwrap = (res) => Array.isArray(res) ? res : (res.data || res.languages || []);

            const subjectsMap = unwrap(subjectsData).reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {});
            const streamsMap = unwrap(streamsData).reduce((acc, m) => ({ ...acc, [m.id]: m.name }), {});
            const specializationsMap = unwrap(specializationsData).reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {});

            const languagesList = unwrap(languagesData);

            setIsEditorialBoard(true); // If we reached here, API allowed it

            // Normalize data (handle naming differences between API and UI)
            const normalizedArticles = (Array.isArray(availableArticles) ? availableArticles : []).map(article => {
                // Robust language resolution
                let langName = 'N/A';
                const rawLang = article.language || article.language_name || article.articleLanguage;

                if (rawLang) {
                    if (typeof rawLang === 'object') {
                        langName = rawLang.name || 'N/A';
                    } else {
                        // Try to find by ID, code, or name (case-insensitive)
                        const matchedLang = languagesList.find(l =>
                            (l.id === rawLang || l._id === rawLang) ||
                            (l.code && String(l.code).toLowerCase() === String(rawLang).toLowerCase()) ||
                            (l.name && String(l.name).toLowerCase() === String(rawLang).toLowerCase())
                        );
                        langName = matchedLang ? matchedLang.name : rawLang;
                    }
                }

                // Robust review/rating extraction
                const reviewFromList = article.reviews && article.reviews.length > 0 ? article.reviews[0] : null;

                const apiRating = article.rating ??
                    article.reviewRating ??
                    article.score ??
                    article.avgRating ??
                    article.review?.rating ??
                    reviewFromList?.rating;

                const apiComments = article.comments ??
                    article.review_comment ??
                    article.comment ??
                    article.review?.comments ??
                    article.review?.comment ??
                    reviewFromList?.comments ??
                    '';

                const apiStatus = (article.status || article.review_status || '').toLowerCase();
                const isReviewedStatus = apiStatus === 'reviewed' || apiStatus === 'checked' || apiStatus === 'approved';

                return {
                    ...article,
                    id: article.id,
                    paperTitle: article.title || article.paper_title || article.paperTitle,
                    subject: subjectsMap[article.subject_id] || article.subject?.name || article.subject_name || article.subject || 'N/A',
                    stream: streamsMap[article.stream_id] || streamsMap[article.major_id] || article.stream?.name || article.stream || article.major?.name || article.major_name || article.major || 'N/A',
                    specialization: specializationsMap[article.specialization_id] || article.specialization?.name || article.specialization_name || article.specialization || 'N/A',
                    articleLanguage: langName,
                    submittedAt: article.submission_date || article.created_at || article.submittedAt,
                    university: article.university || article.university_name || 'N/A',
                    college: article.college_name || article.college || 'N/A',
                    students: (article.students && Array.isArray(article.students) && article.students.length > 0) ? article.students :
                        (() => {
                            const authors = article.authors || [];
                            if (Array.isArray(authors) && authors.length > 0) {
                                return authors.map(a => a ? ({ ...a, name: a.name || a.authorName || a.fullName }) : null).filter(Boolean);
                            }
                            if (typeof authors === 'string' && authors.length > 0) {
                                try {
                                    const parsed = JSON.parse(authors);
                                    if (Array.isArray(parsed)) return parsed.map(a => a ? ({ ...a, name: a.name || a.authorName || a.fullName }) : null).filter(Boolean);
                                    return parsed ? [{ name: parsed.name || parsed }] : [];
                                } catch (e) {
                                    return authors.split(',').map(name => ({ name: name.trim() }));
                                }
                            }
                            return article.submitter ? [{ name: article.submitter.full_name || article.submitter.name || 'Unknown', email: article.submitter.email }] : [];
                        })(),
                    uploadedFile: article.file_url ? {
                        name: article.file_url.split('/').pop(),
                        url: article.file_url
                    } : null,
                    // Consolidate review info for UI consistency
                    review: article.review || (apiRating !== undefined || isReviewedStatus ? {
                        id: reviewFromList?.id || article.review?.id || reviewFromList?._id || article.review?._id,
                        rating: apiRating !== undefined ? Number(apiRating) : 0,
                        comments: apiComments,
                        reviewedAt: article.review?.reviewedAt ||
                            reviewFromList?.created_at ||
                            article.updated_at ||
                            article.created_at ||
                            (isReviewedStatus ? new Date().toISOString() : null)
                    } : null),
                    // Keep top level rating for extra safety
                    rating: apiRating !== undefined ? Number(apiRating) : article.rating
                };
            });

            // Extract unique filter options
            const uniqueUniversities = [...new Set(normalizedArticles.map(a => a.university).filter(Boolean))];
            const uniqueColleges = [...new Set(normalizedArticles.map(a => a.college).filter(Boolean))];
            const uniqueTitles = [...new Set(normalizedArticles.map(a => a.paperTitle).filter(Boolean))];
            const uniqueSubjects = [...new Set(normalizedArticles.map(a => a.subject).filter(Boolean))];

            setUniversities(uniqueUniversities);
            setColleges(uniqueColleges);
            setTitles(uniqueTitles);
            setSubjects(uniqueSubjects);

            // Sort by submission date (newest first)
            const sortedArticles = normalizedArticles.sort((a, b) => {
                const dateA = new Date(a.submittedAt);
                const dateB = new Date(b.submittedAt);
                return dateB - dateA;
            });

            setAllArticles(sortedArticles);
            setFilteredArticles(sortedArticles);
        } catch (error) {
            console.error('Error loading articles:', error);
            setIsEditorialBoard(false);
            showToast('You must be an Editorial Board member to access this page', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let results = allArticles;

        // Apply student name search
        if (searchStudentName.trim() !== '') {
            const term = searchStudentName.toLowerCase();
            results = results.filter(article =>
                article.students && article.students.some(student =>
                    student.name.toLowerCase().includes(term)
                )
            );


        }

        // Apply university filter
        if (selectedUniversity !== 'all') {
            results = results.filter(article => article.university === selectedUniversity);
        }

        // Apply college filter
        if (selectedCollege !== 'all') {
            results = results.filter(article => article.college === selectedCollege);
        }

        // Apply title filter
        if (selectedTitle !== 'all') {
            results = results.filter(article => article.paperTitle === selectedTitle);
        }

        // Apply subject filter
        if (selectedSubject !== 'all') {
            results = results.filter(article => article.subject === selectedSubject);
        }

        setFilteredArticles(results);
    }, [searchStudentName, selectedUniversity, selectedCollege, selectedTitle, selectedSubject, allArticles]);

    const getFileIcon = (fileName) => {
        if (!fileName) return null;
        const extension = fileName.split('.').pop().toLowerCase();
        if (extension === 'pdf') {
            return <FaFilePdf className="text-red-500 text-xl" />;
        } else if (extension === 'doc' || extension === 'docx') {
            return <FaFileWord className="text-blue-500 text-xl" />;
        }
        return <FaFileAlt className="text-gray-500 text-xl" />;
    };

    // Helper function to get language badge color
    const getLanguageBadgeColor = (language) => {
        if (!language) return 'bg-gray-500';

        const colors = {
            'English': 'bg-blue-500',
            'Hindi': 'bg-green-500',
            'Marathi': 'bg-orange-500',
            'Bengali': 'bg-red-500',
            'Tamil': 'bg-purple-500',
            'Telugu': 'bg-pink-500',
            'Gujarati': 'bg-yellow-500',
            'Kannada': 'bg-indigo-500',
            'Malayalam': 'bg-teal-500',
            'Urdu': 'bg-rose-500',
            'Sanskrit': 'bg-amber-500',
            'Punjabi': 'bg-lime-500',
            'Odia': 'bg-cyan-500',
            'Assamese': 'bg-emerald-500',
            'Maithili': 'bg-violet-500'
        };
        return colors[language] || 'bg-gray-500';
    };

    const handleDownloadFile = (article) => {
        if (article.uploadedFile && article.uploadedFile.url) {
            try {
                const link = document.createElement('a');
                link.href = article.uploadedFile.url;
                link.download = article.uploadedFile.name;
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                showToast(`Downloading ${article.uploadedFile.name}`, 'success');
            } catch (error) {
                console.error('Download error:', error);
                showToast('Could not download file. Please try again.', 'error');
            }
        } else {
            showToast('No file available for download', 'error');
        }
    };

    const handleReviewClick = (article) => {
        setSelectedArticle(article);
        setReviewComment('');
        setRating('');
        setDecision('APPROVED');
        setShowReviewModal(true);
    };

    const handleCardReviewClick = (article) => {
        setSelectedArticle(article);
        setReviewComment('');
        setRating('');
        setDecision('APPROVED');
        setShowReviewModal(true);

    };

    const confirmReview = async () => {
        if (!selectedArticle) return;

        // Validation
        if (rating === '' || rating === 0) {
            showToast('Please enter rating (0-10)', 'error');
            return;
        }
        const numRating = parseFloat(rating);
        if (isNaN(numRating) || numRating < 0 || numRating > 10) {
            showToast('Rating must be between 0 and 10', 'error');
            return;
        }

        // Check if rating is in increments of 0.25
        if ((numRating * 100) % 25 !== 0) {
            showToast('Rating must be in increments of 0.25 (e.g., 4.25, 4.50, 4.75)', 'error');
            return;
        }

        try {
            await professorService.submitReview(selectedArticle.id, {
                rating: numRating,
                comments: reviewComment || "No comments", // Backend expects 'comments' (plural)
                decision: decision // Backend expects 'decision' (APPROVED/REJECTED)
            });

            // Refresh the data from API
            await loadAllArticles();

            setShowReviewModal(false);
            setSelectedArticle(null);
            setReviewComment('');
            setRating('');
            setDecision('APPROVED');

            showToast(`Article reviewed successfully with ${numRating.toFixed(2)}/10 rating!`, 'success');
        } catch (error) {
            console.error('Error submitting review:', error);

            // Detailed error parsing for Zod validation errors (as seen in user screenshot)
            let displayError = error.message || 'Failed to submit review';

            if (error.error) {
                try {
                    // The error comes as a stringified JSON array in some cases
                    const parsed = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        displayError = parsed.map(err => `${err.path.join('.')}: ${err.message}`).join(' | ');
                    }
                } catch (e) {
                    // If not valid JSON, use the raw error string
                    displayError = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
                }
            }

            showToast(displayError, 'error');
        }
    };

    const handleEditRating = (article) => {
        setEditingRating(article.id);
        const currentRating = article.review?.rating || 0;
        setTempRating(currentRating.toString());
    };

    const saveRating = async (articleId) => {
        const numRating = parseFloat(tempRating);
        if (isNaN(numRating) || numRating < 0 || numRating > 10) {
            showToast('Rating must be between 0 and 10', 'error');
            return;
        }

        // Check if rating is in increments of 0.25
        if ((numRating * 100) % 25 !== 0) {
            showToast('Rating must be in increments of 0.25 (e.g., 4.25, 4.50, 4.75)', 'error');
            return;
        }

        try {
            // Find the article to get existing content if any
            const article = allArticles.find(a => a.id === articleId);
            const reviewId = article?.review?.id;

            if (reviewId) {
                await professorService.updateReviewRating(reviewId, numRating);
            } else {
                // Fallback to submitReview if no review ID found (shouldn't happen for edit)
                await professorService.submitReview(articleId, {
                    rating: numRating,
                    comments: article?.review?.comments || article?.comments || "Rating updated",
                    decision: 'APPROVED'
                });
            }

            // Refresh the data from API
            await loadAllArticles();
            setEditingRating(null);
            setTempRating('');
            showToast('Rating updated successfully!', 'success');
        } catch (error) {
            console.error('Error updating rating:', error);
            showToast(error.message || 'Failed to update rating', 'error');
        }
    };

    const cancelEditRating = () => {
        setEditingRating(null);
        setTempRating('');
    };

    const toggleRowSelection = (articleId) => {
        setSelectedRows(prev => {
            if (prev.includes(articleId)) {
                return prev.filter(id => id !== articleId);
            } else {
                return [...prev, articleId];
            }
        });
    };

    const toggleSelectAll = () => {
        if (selectedRows.length === filteredArticles.length) {
            setSelectedRows([]);
        } else {
            setSelectedRows(filteredArticles.map(article => article.id));
        }
    };

    const showSelectedArticles = () => {
        if (selectedRows.length === 0) {
            showToast('Please select at least one article', 'error');
            return;
        }
        setViewMode('card');
        setCurrentCardIndex(0);
        setShowSelectedModal(false);
    };

    const getSelectedArticles = () => {
        return filteredArticles.filter(article => selectedRows.includes(article.id));
    };

    const goToTable = () => {
        setViewMode('table');
        setSelectedRows([]);
        setCurrentCardIndex(0);
    };

    const nextCard = () => {
        const selectedArticles = getSelectedArticles();
        if (currentCardIndex < selectedArticles.length - 1) {
            setCurrentCardIndex(currentCardIndex + 1);
        }
    };

    const prevCard = () => {
        if (currentCardIndex > 0) {
            setCurrentCardIndex(currentCardIndex - 1);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const clearFilters = () => {
        setSearchStudentName('');
        setSelectedUniversity('all');
        setSelectedCollege('all');
        setSelectedTitle('all');
        setSelectedSubject('all');
    };

    const isArticleReviewed = (article) => {
        const status = (article.status || article.review_status || '').toLowerCase();
        const hasStatus = status === 'reviewed' || status === 'checked' || status === 'approved';
        const hasRating = article.rating !== undefined && article.rating !== null;
        const hasReviewObject = article.review && (article.review.reviewedAt || article.review.rating !== undefined);

        return hasStatus || hasRating || hasReviewObject;
    };

    const getArticleRating = (article) => {
        // Check nested review object
        if (article.review && article.review.rating !== undefined) {
            return Number(article.review.rating) || 0;
        }
        // Check reviews array
        if (article.reviews && article.reviews.length > 0 && article.reviews[0].rating !== undefined) {
            return Number(article.reviews[0].rating) || 0;
        }
        // Check top level rating
        if (article.rating !== undefined && article.rating !== null) {
            return Number(article.rating) || 0;
        }
        return 0;
    };

    const darkGradient = {
        background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
    };

    const renderCardView = () => {
        const selectedArticles = getSelectedArticles();
        if (selectedArticles.length === 0) {
            return (
                <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                    <FaFileAlt className="text-6xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">No Articles Selected</h3>
                    <p className="text-gray-600 mb-4">
                        Please select articles from the table to review them in card view.
                    </p>
                    <button
                        onClick={goToTable}
                        className="mt-4 px-6 py-2 bg-primary-blue text-white rounded-lg hover:bg-secondary-blue transition-all"
                    >
                        Go to Table
                    </button>
                </div>
            );
        }

        const article = selectedArticles[currentCardIndex];
        const isReviewed = isArticleReviewed(article);
        const articleRating = getArticleRating(article);

        return (
            <div className="space-y-6">
                {/* Card View Header */}
                <div className="rounded-2xl shadow-lg p-6" style={darkGradient}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={goToTable}
                                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all"
                            >
                                <FaArrowLeft />
                                Back to Table
                            </button>
                            <div>
                                <h2 className="text-2xl font-bold text-white">Review Selected Articles</h2>
                                <p className="text-blue-100">
                                    Card {currentCardIndex + 1} of {selectedArticles.length}
                                </p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-white text-sm">Remaining</p>
                            <p className="text-2xl font-bold text-white">
                                {selectedArticles.filter(a => !isArticleReviewed(a)).length}/{selectedArticles.length}
                            </p>
                        </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="flex items-center justify-center gap-4 mt-4">
                        <button
                            onClick={prevCard}
                            disabled={currentCardIndex === 0}
                            className={`px-4 py-2 rounded-lg font-semibold transition-all ${currentCardIndex === 0
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-white text-primary-blue hover:bg-blue-50'
                                }`}
                        >
                            Previous
                        </button>

                        <div className="flex gap-1">
                            {selectedArticles.map((_, index) => (
                                <div
                                    key={index}
                                    className={`w-3 h-3 rounded-full ${index === currentCardIndex
                                        ? 'bg-white'
                                        : isArticleReviewed(selectedArticles[index])
                                            ? 'bg-green-400'
                                            : 'bg-white/50'
                                        }`}
                                />
                            ))}
                        </div>

                        <button
                            onClick={nextCard}
                            disabled={currentCardIndex === selectedArticles.length - 1}
                            className={`px-4 py-2 rounded-lg font-semibold transition-all ${currentCardIndex === selectedArticles.length - 1
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-white text-primary-blue hover:bg-blue-50'
                                }`}
                        >
                            Next
                        </button>
                    </div>
                </div>

                {/* Article Card - 6 Column Grid */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="p-6">
                        {/* Header */}
                        <div className="flex flex-wrap justify-between items-start mb-6 gap-3">
                            <div className="flex items-center gap-4 flex-wrap">
                                {/* Language Badge */}
                                {article.articleLanguage && (
                                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold shadow-md text-white ${getLanguageBadgeColor(article.articleLanguage)}`}>
                                        <FaLanguage />
                                        {article.articleLanguage}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Article Details - 6 Column Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
                            {/* Column 1: Article Title */}
                            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-xl border-2 border-indigo-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <FaBook className="text-indigo-600" />
                                    <p className="text-xs font-semibold text-gray-500">Article Title</p>
                                </div>
                                <p className="font-bold text-gray-900 text-sm line-clamp-4">{article.paperTitle}</p>
                            </div>

                            {/* Column 2: Subject */}
                            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-4 rounded-xl border-2 border-teal-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <FaBook className="text-teal-600" />
                                    <p className="text-xs font-semibold text-gray-500">Department</p>
                                </div>
                                <p className="font-bold text-gray-900 text-sm">{article.subject}</p>
                            </div>

                            {/* Column 3: Language */}
                            <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-4 rounded-xl border-2 border-violet-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <FaLanguage className="text-violet-600" />
                                    <p className="text-xs font-semibold text-gray-500">Language</p>
                                </div>
                                <div className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-bold text-white ${getLanguageBadgeColor(article.articleLanguage)}`}>
                                    {article.articleLanguage || 'Not specified'}
                                </div>
                            </div>

                            {/* Column 4: Students */}
                            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-xl border-2 border-blue-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <FaUser className="text-blue-600" />
                                    <p className="text-xs font-semibold text-gray-500">Students</p>
                                </div>
                                <div className="space-y-1">
                                    {article.students && article.students.map((student, index) => (
                                        <p key={index} className="font-semibold text-gray-900 text-sm">
                                            {student.name}
                                        </p>
                                    ))}
                                </div>
                            </div>

                            {/* Column 5: Content */}
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border-2 border-green-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <FaFileAlt className="text-green-600" />
                                    <p className="text-xs font-semibold text-gray-500">Content</p>
                                </div>
                                {article.uploadedFile ? (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            {getFileIcon(article.uploadedFile.name)}
                                            <p className="text-xs font-bold text-gray-900 truncate" title={article.uploadedFile.name}>
                                                {article.uploadedFile.name}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => handleDownloadFile(article)}
                                            className="w-full flex items-center justify-center gap-1 px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-all"
                                        >
                                            <FaDownload className="text-xs" />
                                            Download
                                        </button>
                                    </div>
                                ) : (
                                    <p className="text-xs text-gray-400">No content</p>
                                )}
                            </div>

                            {/* Column 6: Article Rating */}
                            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-4 rounded-xl border-2 border-yellow-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <FaStar className="text-yellow-600" />
                                    <p className="text-xs font-semibold text-gray-500">Article Impact</p>
                                </div>
                                {editingRating === article.id ? (
                                    <div className="space-y-2">
                                        <input
                                            type="number"
                                            min="0"
                                            max="10"
                                            step="0.25"
                                            value={tempRating}
                                            onChange={(e) => setTempRating(e.target.value)}
                                            className="w-full px-2 py-1 border-2 border-yellow-300 rounded text-center font-bold text-sm"
                                            placeholder="0.00"
                                        />
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => saveRating(article.id)}
                                                className="flex-1 p-1 bg-green-500 text-white rounded hover:bg-green-600 text-xs"
                                            >
                                                <FaCheck />
                                            </button>
                                            <button
                                                onClick={cancelEditRating}
                                                className="flex-1 p-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                                            >
                                                <FaTimes />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-center gap-1">
                                            <span className="text-2xl font-bold text-gray-900">
                                                {isReviewed ? articleRating.toFixed(2) : '0.00'}
                                            </span>
                                            <span className="text-gray-500 text-sm">/10</span>
                                        </div>
                                        {isReviewed && (
                                            <button
                                                onClick={() => handleEditRating(article)}
                                                className="w-full flex items-center justify-center gap-1 px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                            >
                                                <FaEdit className="text-xs" />
                                                Edit
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-100">
                            <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                                {article.submittedAt && (
                                    <div className="flex items-center gap-1">
                                        <FaCalendarAlt />
                                        <span>Submitted: {formatDate(article.submittedAt)}</span>
                                    </div>
                                )}
                                {article.university && (
                                    <div className="flex items-center gap-1">
                                        <FaUniversity />
                                        <span>{article.university}</span>
                                    </div>
                                )}
                                {article.college && (
                                    <div className="flex items-center gap-1">
                                        <FaGraduationCap />
                                        <span>{article.college}</span>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-2">
                                {isReviewed ? (
                                    <span className="flex items-center gap-2 px-4 py-3 bg-green-100 text-green-700 rounded-lg font-bold">
                                        <FaCheckCircle />
                                        Reviewed
                                    </span>
                                ) : (
                                    <button
                                        onClick={() => handleCardReviewClick(article)}
                                        className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-bold"
                                    >
                                        <FaStar />
                                        Review
                                    </button>
                                )}
                                <button
                                    onClick={goToTable}
                                    className="flex items-center gap-2 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-all font-bold"
                                >
                                    <FaArrowLeft />
                                    Go to Table
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderTableView = () => (
        <>
            {/* Articles Count */}
            <div className="mb-4 flex items-center justify-between">
                <p className="text-gray-600">
                    Showing <span className="font-bold">{filteredArticles.length}</span> of{' '}
                    <span className="font-bold">{allArticles.length}</span> articles
                </p>
                {selectedRows.length > 0 && (
                    <button
                        onClick={showSelectedArticles}
                        className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-medium"
                    >
                        View Selected ({selectedRows.length})

                    </button>



                )}
            </div>

            {/* Articles Table */}
            {filteredArticles.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                    <FaFileAlt className="text-6xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">No Articles Found</h3>
                    <p className="text-gray-600 mb-4">
                        {!isEditorialBoard
                            ? 'You need to be in the Editorial Board to view all articles'
                            : 'Try adjusting your filters'}
                    </p>
                    {isEditorialBoard && (
                        <button
                            onClick={clearFilters}
                            className="mt-4 px-6 py-2 bg-primary-blue text-white rounded-lg hover:bg-secondary-blue transition-all"
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="text-white" style={darkGradient}>
                                <tr>
                                    <th className="px-4 py-4 text-left">
                                        <input
                                            type="checkbox"
                                            checked={selectedRows.length === filteredArticles.length && filteredArticles.length > 0}
                                            onChange={toggleSelectAll}
                                            className="w-5 h-5 rounded border-white cursor-pointer"
                                        />
                                    </th>
                                    <th className="px-4 py-4 text-left font-semibold">Student Name</th>
                                    <th className="px-4 py-4 text-left font-semibold">University</th>
                                    <th className="px-4 py-4 text-left font-semibold">Article Title</th>
                                    <th className="px-4 py-4 text-left font-semibold">Department</th>
                                    <th className="px-4 py-4 text-left font-semibold">Stream</th>
                                    <th className="px-4 py-4 text-left font-semibold">Specialization</th>
                                    <th className="px-4 py-4 text-left font-semibold">Language</th>
                                    <th className="px-4 py-4 text-left font-semibold">Download</th>
                                    <th className="px-4 py-4 text-left font-semibold">Article Impact</th>
                                    <th className="px-4 py-4 text-left font-semibold">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredArticles.map((article, index) => {
                                    return (
                                        <tr
                                            key={article.id}
                                            className={`hover:bg-blue-50 transition-all ${selectedRows.includes(article.id) ? 'bg-blue-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                                }`}
                                        >
                                            <td className="px-4 py-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRows.includes(article.id)}
                                                    onChange={() => toggleRowSelection(article.id)}
                                                    className="w-5 h-5 rounded border-gray-300 cursor-pointer"
                                                />
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex items-center gap-2">
                                                    <FaUser className="text-blue-500" />
                                                    <span className="font-semibold text-gray-900">
                                                        {article.students?.map(s => s.name).join(', ')}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex items-center gap-2">
                                                    <FaUniversity className="text-purple-500" />
                                                    <span className="text-gray-700">{article.university || 'N/A'}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="max-w-xs">
                                                    <p className="font-semibold text-gray-900 line-clamp-2" title={article.paperTitle}>
                                                        {article.paperTitle}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold">
                                                    {article.subject}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold">
                                                    {article.stream}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-semibold">
                                                    {article.specialization}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold text-white ${getLanguageBadgeColor(article.articleLanguage)}`}>
                                                    <FaLanguage className="text-xs" />
                                                    {article.articleLanguage || 'Not specified'}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                {article.uploadedFile ? (
                                                    <button
                                                        onClick={() => handleDownloadFile(article)}
                                                        className="flex items-center gap-2 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all text-sm font-medium"
                                                    >
                                                        {getFileIcon(article.uploadedFile.name)}
                                                        <FaDownload />
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-400 text-sm">No file</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4">
                                                {editingRating === article.id ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="10"
                                                            step="0.25"
                                                            value={tempRating}
                                                            onChange={(e) => setTempRating(e.target.value)}
                                                            className="w-20 px-2 py-1 border-2 border-blue-300 rounded text-center font-bold"
                                                            placeholder="0.00"
                                                        />
                                                        <button
                                                            onClick={() => saveRating(article.id)}
                                                            className="p-1 bg-green-500 text-white rounded hover:bg-green-600"
                                                        >
                                                            <FaCheck />
                                                        </button>
                                                        <button
                                                            onClick={cancelEditRating}
                                                            className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                                                        >
                                                            <FaTimes />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex items-center gap-1">
                                                            <FaStar className="text-yellow-500" />
                                                            <span className="font-bold text-gray-900">
                                                                {getArticleRating(article).toFixed(2)}
                                                            </span>
                                                            <span className="text-gray-500">/10</span>
                                                        </div>
                                                        {isArticleReviewed(article) && (
                                                            <button
                                                                onClick={() => handleEditRating(article)}
                                                                className="p-1 text-blue-500 hover:text-blue-600"
                                                                title="Edit article impact"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-4">
                                                {isArticleReviewed(article) ? (
                                                    <span className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg font-semibold">
                                                        <FaCheckCircle />
                                                        Reviewed
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleReviewClick(article)}
                                                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-semibold"
                                                    >
                                                        <FaStar />
                                                        Review
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>

    );

    return (
        <>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {/* Review Modal */}
            {showReviewModal && selectedArticle && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 animate-fade-in max-h-[90vh] overflow-y-auto">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-blue-100">
                                <FaCheckCircle className="w-8 h-8 text-blue-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Review Article</h3>
                            <p className="text-gray-600 mb-4">Provide your impact assessment and feedback for this article</p>

                            <div className="bg-gray-50 p-3 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-gray-900">{selectedArticle.paperTitle}</p>
                                <div className="flex items-center justify-center gap-2 mt-2">
                                    <span className="text-xs text-gray-600 font-medium">
                                        Submitted by: {selectedArticle.students?.map(s => s.name).join(', ') || 'Unknown'}
                                    </span>
                                    {selectedArticle.articleLanguage && (
                                        <span className={`text-xs px-2 py-0.5 rounded-full text-white ${getLanguageBadgeColor(selectedArticle.articleLanguage)}`}>
                                            {selectedArticle.articleLanguage}
                                        </span>
                                    )}
                                </div>
                            </div>

                        </div>


                        {/* Rating Input */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                                Article Impact (0 to 10) <span className="text-red-500">*</span>
                                <span className="text-xs text-gray-500 ml-2">(increments of 0.25)</span>
                            </label>
                            <div className="p-4 rounded-xl border-2 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
                                <div className="flex items-center justify-center gap-3 mb-4">
                                    <FaStar className="text-3xl text-blue-500" />
                                    <input
                                        type="number"
                                        min="0"
                                        max="10"
                                        step="0.25"
                                        value={rating}
                                        onChange={(e) => setRating(e.target.value)}
                                        className="w-32 text-center text-4xl font-bold text-gray-900 border-2 border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-primary-blue focus:border-primary-blue"
                                        placeholder="0.00"
                                    />
                                    <span className="text-gray-600">/ 10</span>
                                </div>
                                <p className="text-xs text-center text-gray-600">Enter impact score between 0 and 10 in increments of 0.25</p>
                                <div className="flex justify-center gap-2 mt-2 flex-wrap">
                                    {['7.00', '7.50', '8.00', '8.50', '9.00', '9.50', '10.00'].map(val => (
                                        <button
                                            key={val}
                                            onClick={() => setRating(val)}
                                            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                        >
                                            {val}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Comments */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Comments <span className="text-gray-400 text-xs font-normal ml-1">(Optional)</span>
                            </label>
                            <textarea
                                value={reviewComment}
                                onChange={(e) => setReviewComment(e.target.value)}
                                rows="4"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all"
                                placeholder="Enter your comments here..."
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowReviewModal(false);
                                    setReviewComment('');
                                    setRating('');
                                }}
                                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmReview}
                                className="flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-all bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                            >
                                Submit Review
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6">
                <div className="max-w mx-auto px-4">
                    {/* Header */}
                    <div className="rounded-2xl shadow-lg p-6 mb-6" style={darkGradient}>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">All Articles - Young India</h1>
                                <p className="text-blue-100">Browse and review all submitted articles</p>
                                {isEditorialBoard && (
                                    <div className="flex items-center gap-2 mt-2">
                                        <div className="px-3 py-1 bg-white/20 rounded-lg flex items-center gap-2">
                                            <FaUserTie className="text-white" />
                                            <span className="text-white text-sm font-semibold">Editorial Board Access</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                    <FaUserTie className="text-2xl text-white" />
                                </div>
                                <div className="text-right">
                                    <p className="text-white text-sm">Total Articles</p>
                                    <p className="text-2xl font-bold text-white">{allArticles.length}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filters and Search */}
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            {/* Student Name Search */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Student Name</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchStudentName}
                                        onChange={(e) => setSearchStudentName(e.target.value)}
                                        placeholder="Search by student name..."
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all"
                                    />
                                    <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                </div>
                            </div>

                            {/* University Filter */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">University</label>
                                <div className="relative">
                                    <select
                                        value={selectedUniversity}
                                        onChange={(e) => setSelectedUniversity(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all bg-white appearance-none"
                                    >
                                        <option value="all">All Universities</option>
                                        {universities.map((uni) => (
                                            <option key={uni} value={uni}>
                                                {uni}
                                            </option>
                                        ))}
                                    </select>
                                    <FaUniversity className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                </div>
                            </div>

                            {/* College Filter */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">College</label>
                                <div className="relative">
                                    <select
                                        value={selectedCollege}
                                        onChange={(e) => setSelectedCollege(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all bg-white appearance-none"
                                    >
                                        <option value="all">All Colleges</option>
                                        {colleges.map((col) => (
                                            <option key={col} value={col}>
                                                {col}
                                            </option>
                                        ))}
                                    </select>
                                    <FaGraduationCap className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                </div>
                            </div>

                            {/* Title Filter */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Article Title</label>
                                <div className="relative">
                                    <select
                                        value={selectedTitle}
                                        onChange={(e) => setSelectedTitle(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all bg-white appearance-none"
                                    >
                                        <option value="all">All Titles</option>
                                        {titles.map((title) => (
                                            <option key={title} value={title}>
                                                {title}
                                            </option>
                                        ))}
                                    </select>
                                    <FaBook className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                </div>
                            </div>

                            {/* Subject Filter */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Department</label>
                                <div className="relative">
                                    <select
                                        value={selectedSubject}
                                        onChange={(e) => setSelectedSubject(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all bg-white appearance-none"
                                    >
                                        <option value="all">All Departments</option>
                                        {subjects.map((subject) => (
                                            <option key={subject} value={subject}>
                                                {subject}
                                            </option>
                                        ))}
                                    </select>
                                    <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                </div>
                            </div>
                        </div>

                        {(searchStudentName || selectedUniversity !== 'all' || selectedCollege !== 'all' || selectedTitle !== 'all' || selectedSubject !== 'all') && (
                            <div className="mt-4 flex gap-3">
                                <button
                                    onClick={clearFilters}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all font-medium"
                                >
                                    Clear All Filters
                                </button>
                                {viewMode === 'table' && selectedRows.length > 0 && (
                                    <button
                                        onClick={showSelectedArticles}
                                        className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-medium"
                                    >
                                        Show Selected ({selectedRows.length})
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Loading State */}
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <FaSpinner className="text-4xl text-primary-blue animate-spin" />
                        </div>
                    ) : (
                        <>
                            {viewMode === 'card' ? renderCardView() : renderTableView()}
                        </>
                    )}
                </div>
            </div>
        </>
    );

};

export default AllArticlesYoungIndia;